/**
 * Softphone core shared by the web app and (from phase 2) the mobile app.
 * Wraps JsSIP: SIP over secure WebSocket for signalling, WebRTC for audio.
 * It knows nothing about screens; the app renders state from the events.
 */
import JsSIP from 'jssip';
import type { DTMF_TRANSPORT } from 'jssip/lib/Constants';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { RTCSessionEvent } from 'jssip/lib/UA';

/**
 * How long to wait for network routes before sending a call's audio offer.
 * JsSIP waits for every route by default, and a TURN server that can't be
 * reached would hold each call for half a minute or more.
 */
const ICE_WAIT_MS = 2000;
/** How long the audio may stall before the call looks for new network routes. */
const ICE_STALL_MS = 3000;
/** How long new routes may take before the SIP connection itself is replaced. */
const RECOVERY_MS = 6000;

export type RegistrationState = 'stopped' | 'connecting' | 'registered' | 'unregistered' | 'failed';

export type CallState = 'ringing-out' | 'ringing-in' | 'connected' | 'ended';

export interface CallInfo {
  direction: 'incoming' | 'outgoing';
  /** The other party: their number or extension. */
  remote: string;
  /** Their name, when the PBX gives one (a colleague, or a customer's contact card). */
  remoteName?: string;
  state: CallState;
  /** Why the call ended, when state is "ended". */
  endCause?: string;
  /** The PBX's ID for an incoming call, so a notification's "Answer" can pick it up. */
  callId?: string;
  /** This side's microphone is off. */
  muted?: boolean;
  /** Connected, but the network dropped: finding a way back (e.g. after a switch from Wi-Fi to mobile data). */
  reconnecting?: boolean;
}

export interface SoftphoneOptions {
  /** Secure WebSocket address of the PBX, e.g. wss://pbx.example.com/ws */
  websocketUrl: string;
  /** Domain used in SIP addresses. Asterisk identifies a device by its endpoint ID. */
  domain: string;
  /** Generated ID of this device's endpoint. */
  endpointId: string;
  password: string;
  iceServers?: RTCIceServer[];
  /** "relay" sends all audio through the TURN server (for testing it). */
  iceTransportPolicy?: RTCIceTransportPolicy;
  /** Microphone constraints. Defaults to the browser's echo cancellation and noise suppression. */
  audio?: MediaTrackConstraints | true;
  /** Log SIP traffic to the console. */
  debug?: boolean;
}

export interface SoftphoneEvents {
  registration: (state: RegistrationState, detail?: string) => void;
  call: (call: CallInfo | null) => void;
  remoteStream: (stream: MediaStream | null) => void;
}

type ListenerSets = { [K in keyof SoftphoneEvents]: Set<SoftphoneEvents[K]> };

export class Softphone {
  private ua: JsSIP.UA | null = null;
  private session: RTCSession | null = null;
  private call: CallInfo | null = null;
  private remote: MediaStream | null = null;
  private readonly watchedConnections = new WeakSet<RTCPeerConnection>();
  /** Mid-call recovery: restart ICE once the SIP connection is back. */
  private recoverOnConnect = false;
  private restartingIce = false;
  private stallTimer: ReturnType<typeof setTimeout> | undefined;
  private recoveryTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly listeners: ListenerSets = {
    registration: new Set(),
    call: new Set(),
    remoteStream: new Set(),
  };

  constructor(private readonly options: SoftphoneOptions) {}

  on<K extends keyof SoftphoneEvents>(event: K, listener: SoftphoneEvents[K]): () => void {
    this.listeners[event].add(listener);
    return () => this.listeners[event].delete(listener);
  }

  /** The call in progress: none once it has ended (the "ended" event still tells how). */
  get currentCall(): CallInfo | null {
    return this.call?.state === 'ended' ? null : this.call;
  }

  get remoteStream(): MediaStream | null {
    return this.remote;
  }

  start(): void {
    if (this.ua) return;
    if (this.options.debug) JsSIP.debug.enable('JsSIP:*');
    else JsSIP.debug.disable();

    const ua = new JsSIP.UA({
      sockets: [new JsSIP.WebSocketInterface(this.options.websocketUrl)],
      uri: `sip:${this.options.endpointId}@${this.options.domain}`,
      password: this.options.password,
      register: true,
      register_expires: 300,
      session_timers: false,
      user_agent: 'Softphone',
      connection_recovery_min_interval: 2,
      connection_recovery_max_interval: 30,
    });

    ua.on('connecting', () => this.emit('registration', 'connecting'));
    ua.on('connected', () => {
      if (!this.recoverOnConnect) return;
      this.recoverOnConnect = false;
      this.restartIce();
    });
    ua.on('disconnected', () => {
      this.emit('registration', 'connecting', 'connection lost');
      // A call keeps going: when the connection is back, its audio gets new routes.
      if (this.session && this.call?.state === 'connected') {
        this.recoverOnConnect = true;
        this.updateCall({ reconnecting: true });
      }
    });
    ua.on('registered', () => this.emit('registration', 'registered'));
    ua.on('unregistered', () => this.emit('registration', 'unregistered'));
    ua.on('registrationFailed', (event) => this.emit('registration', 'failed', event.cause));
    ua.on('newRTCSession', (event: RTCSessionEvent) => this.onNewSession(event));

    this.ua = ua;
    ua.start();
  }

  stop(): void {
    clearTimeout(this.stallTimer);
    clearTimeout(this.recoveryTimer);
    this.ua?.stop();
    this.ua = null;
    this.emit('registration', 'stopped');
  }

  /**
   * A new SIP connection now, e.g. when the network changed: registration
   * follows, and a call in progress gets new routes for its audio.
   */
  reconnect(): void {
    const transport = (this.ua as unknown as { transport?: { connect(): void; disconnect(): void } } | null)?.transport;
    if (!transport) return;
    if (this.session && this.call?.state === 'connected') {
      this.recoverOnConnect = true;
      this.updateCall({ reconnecting: true });
    }
    transport.disconnect();
    transport.connect();
  }

  /** Places a call to a number or extension. */
  dial(destination: string): void {
    if (!this.ua) throw new Error('Softphone is not started');
    if (this.session) throw new Error('A call is already in progress');
    this.ua.call(`sip:${destination}@${this.options.domain}`, {
      mediaConstraints: this.mediaConstraints(),
      pcConfig: this.pcConfig(),
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
    });
  }

  answer(): void {
    if (!this.session || this.call?.state !== 'ringing-in') return;
    this.session.answer({ mediaConstraints: this.mediaConstraints(), pcConfig: this.pcConfig() });
  }

  /** Stops (or restarts) sending this side's audio during a call. */
  setMuted(muted: boolean): void {
    if (!this.session) return;
    if (muted) this.session.mute({ audio: true });
    else this.session.unmute({ audio: true });
    this.updateCall({ muted });
  }

  /**
   * Presses keys during a call (a menu's choices): sent in the audio stream
   * as telephone events (RFC 4733), as phones on carrier lines send them.
   */
  sendKeys(keys: string): void {
    if (!this.session || this.call?.state !== 'connected' || !/^[0-9*#]+$/.test(keys)) return;
    this.session.sendDTMF(keys, { transportType: 'RFC2833' as DTMF_TRANSPORT, duration: 160, interToneGap: 80 });
  }

  /** Ends the current call, or declines it while it is ringing. */
  hangup(): void {
    this.session?.terminate();
  }

  private onNewSession(event: RTCSessionEvent): void {
    const session = event.session;

    // One call at a time for now. Call waiting comes with the call rules in M3.
    if (this.session) {
      if (event.originator === 'remote') session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      return;
    }

    this.session = session;
    const incoming = event.originator === 'remote';
    const callId = incoming ? event.request.getHeader('X-PBX-Call-ID') : undefined;
    const remoteName = incoming ? session.remote_identity.display_name?.trim() : undefined;
    this.setCall({
      direction: incoming ? 'incoming' : 'outgoing',
      remote: session.remote_identity.uri.user,
      ...(remoteName && remoteName !== session.remote_identity.uri.user && { remoteName }),
      state: incoming ? 'ringing-in' : 'ringing-out',
      ...(callId && { callId }),
    });

    // Send as soon as the relay route is known (it is gathered last), or after ICE_WAIT_MS.
    let gathering: { ready: () => void; timer: ReturnType<typeof setTimeout> } | null = null;
    session.on('icecandidate', ({ candidate, ready }) => {
      if (gathering?.ready !== ready) gathering = { ready, timer: setTimeout(ready, ICE_WAIT_MS) };
      if (candidate.type === 'relay' || / typ relay /.test(candidate.candidate)) {
        clearTimeout(gathering.timer);
        ready();
      }
    });

    session.on('peerconnection', ({ peerconnection }) => this.watchConnection(peerconnection));
    // For outgoing calls JsSIP creates the connection before this event fires.
    if (session.connection) this.watchConnection(session.connection);

    session.on('accepted', () => this.updateCall({ state: 'connected' }));
    session.on('confirmed', () => this.updateCall({ state: 'connected' }));
    session.on('ended', (end) => this.endCall(session, end.cause));
    session.on('failed', (end) => this.endCall(session, end.cause));
  }

  private watchConnection(connection: RTCPeerConnection): void {
    if (this.watchedConnections.has(connection)) return;
    this.watchedConnections.add(connection);
    connection.addEventListener('iceconnectionstatechange', () => this.onIceState(connection.iceConnectionState));
    connection.addEventListener('track', (event) => {
      if (event.track.kind !== 'audio') return;
      this.remote = event.streams[0] ?? new MediaStream([event.track]);
      this.emit('remoteStream', this.remote);
    });
  }

  /** The audio's network path: recovered, stalled (wait a moment), or lost (look for new routes). */
  private onIceState(state: RTCIceConnectionState): void {
    if (!this.session || this.call?.state !== 'connected') return;
    clearTimeout(this.stallTimer);
    if (state === 'connected' || state === 'completed') {
      clearTimeout(this.recoveryTimer);
      if (this.call.reconnecting) this.updateCall({ reconnecting: false });
      return;
    }
    if (state === 'disconnected') {
      this.updateCall({ reconnecting: true });
      this.stallTimer = setTimeout(() => this.recover(), ICE_STALL_MS);
    } else if (state === 'failed') {
      this.updateCall({ reconnecting: true });
      this.recover();
    }
  }

  /** New routes over the current connection; if that doesn't help soon, a new connection too. */
  private recover(): void {
    if (!this.ua || !this.session) return;
    if (!this.ua.isConnected()) {
      this.recoverOnConnect = true;
      return;
    }
    this.restartIce();
    clearTimeout(this.recoveryTimer);
    this.recoveryTimer = setTimeout(() => {
      if (this.call?.reconnecting) this.reconnect();
    }, RECOVERY_MS);
  }

  /** A re-INVITE with an ICE restart: both sides gather routes again on the network there is now. */
  private restartIce(): void {
    const session = this.session;
    if (!session || this.restartingIce) return;
    this.restartingIce = true;
    const done = () => (this.restartingIce = false);
    setTimeout(done, RECOVERY_MS);
    if (!session.renegotiate({ rtcOfferConstraints: { iceRestart: true } }, done)) done();
  }

  private endCall(session: RTCSession, cause: string): void {
    if (this.session !== session) return;
    this.session = null;
    this.recoverOnConnect = false;
    clearTimeout(this.stallTimer);
    clearTimeout(this.recoveryTimer);
    this.updateCall({ state: 'ended', endCause: cause });
    this.remote = null;
    this.emit('remoteStream', null);
  }

  private setCall(call: CallInfo | null): void {
    this.call = call;
    this.emit('call', call);
  }

  private updateCall(change: Partial<CallInfo>): void {
    if (this.call) this.setCall({ ...this.call, ...change });
  }

  private mediaConstraints(): MediaStreamConstraints {
    return { audio: this.options.audio ?? true, video: false };
  }

  private pcConfig(): RTCConfiguration {
    return {
      iceServers: this.options.iceServers ?? [],
      iceTransportPolicy: this.options.iceTransportPolicy ?? 'all',
      rtcpMuxPolicy: 'require',
      bundlePolicy: 'max-bundle',
    };
  }

  private emit<K extends keyof SoftphoneEvents>(event: K, ...args: Parameters<SoftphoneEvents[K]>): void {
    for (const listener of this.listeners[event]) {
      (listener as (...a: Parameters<SoftphoneEvents[K]>) => void)(...args);
    }
  }
}
